import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { zumAngebot, type Optionsanfrage } from '../api/optionsanfragen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die Terminoptionen eines Angebots — was angefragt ist und was daraus wurde.
 *
 * ============================================================================
 * HIER WIRD GELESEN, NICHT ANGEFRAGT
 * ============================================================================
 *
 * Auftraggeber, 13.09.2026: „terminoptionen sollen direkt mit dem anlegen
 * eines artikels im angebot angelegt werden … die termine sollen in der
 * angebotsmaske im reiter terminoption noch einmal aufgeführt sein und der
 * status ist zu sehen, aber sie sollen direkt im angebot erstellt werden."
 *
 * Also: Die Anfrage entsteht mit der POSITION (Reiter „Positionen"), und zwar
 * aus demselben Formular, das die Leistung ins Angebot nimmt — wer eine
 * Leistung erfasst, weiß in diesem Moment, wann sie stattfinden soll. Diese
 * Karte zeigt, was daraus geworden ist.
 *
 * ============================================================================
 * DER STATUS IST DIE AUSKUNFT
 * ============================================================================
 *
 * ANG-36: Jede terminbezogene Position trägt entweder den bestätigten Termin
 * samt Haltedatum oder sichtbar den Vermerk „Termin auf Anfrage". Welcher Fall
 * vorliegt, steht hier je Wunschtermin — offen, zugesagt (mit Haltedatum),
 * abgelehnt (mit Grund) oder mit einem Gegenvorschlag der Disposition.
 */

function alsTag(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

export function Terminoptionen({ angebot }: { readonly angebot: string }): JSX.Element {
  const [anfragen, setzeAnfragen] = useState<readonly Optionsanfrage[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeAnfragen(await zumAngebot(angebot));
      setzeFehler(null);
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    } finally {
      setzeLaedt(false);
    }
  }, [angebot]);

  useEffect(() => {
    void holen();
  }, [holen]);

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Terminoptionen</h2>
        {/* ANG-30, ANG-36 */}
      </div>

      {fehler !== null && <Meldung fehler={fehler} />}

      {laedt ? (
        <p className="feld__hilfe">Wird geladen …</p>
      ) : anfragen.length === 0 ? (
        <p className="feld__hilfe">
          Noch keine Anfrage. Wunschtermine tragen Sie bei der Position ein — daraus entsteht
          die Anfrage an die Disposition, und im Kalender steht der Tag als Option. Bis zur
          Zusage steht auf dem Dokument „Termin auf Anfrage".
        </p>
      ) : (
        <ul className="optionen__alternativen">
          {anfragen.map((a) => (
            <li key={a.id} className="optionen__alternative">
              <strong>{a.leistung}</strong> · {a.teilnehmerzahl} Plätze
              {a.standort !== null && <> · {a.standort}</>}
              <p className="hilfe">
                Angefragt von {a.anfragendername}
                {a.antwortfristBis === null
                  ? ' — ohne Antwortfrist'
                  : ` — Antwort erbeten bis ${alsTag(a.antwortfristBis)}`}
                {a.gewuenschteFristBis !== null && (
                  <> · gewünschte Optionsfrist {alsTag(a.gewuenschteFristBis)}</>
                )}
              </p>
              <ul className="optionen__stand">
                {a.alternativen.map((alt) => (
                  <li key={alt.id}>
                    {alsTag(alt.wunschtag)}
                    {alt.wunschzeit !== null && <> um {alt.wunschzeit} Uhr</>}{' '}
                    {alt.antwort === null ? (
                      <span className="marke">auf Anfrage</span>
                    ) : alt.antwort === 'zugesagt' ? (
                      /*
                        ANG-39: Nach der Annahme sind aus „zugesagt" zwei Dinge
                        geworden — der eine Termin ist verbindlich, die übrigen
                        sind verfallen. Beides steht hier, denn beides ist
                        geschehen: Der verfallene Termin ist abgesagt und hat
                        seine Plätze freigegeben.
                      */
                      a.gewaehlteAlternative === null ? (
                        <>
                          <span className="marke marke--gut">zugesagt</span>
                          {alt.fristBis !== null && (
                            <span className="hilfe"> gehalten bis {alsTag(alt.fristBis)}</span>
                          )}
                        </>
                      ) : a.gewaehlteAlternative === alt.id ? (
                        <>
                          <span className="marke marke--gut">angenommen</span>
                          <span className="hilfe">
                            {' '}
                            gewählt von {a.gewaehltername ?? 'unbekannt'}
                            {a.gewaehltAm !== null && ` am ${alsTag(a.gewaehltAm)}`}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="marke">verfallen</span>
                          <span className="hilfe">
                            {' '}
                            mit der Annahme — Plätze frei, Termin abgesagt
                          </span>
                        </>
                      )
                    ) : alt.antwort === 'abgelehnt' ? (
                      <>
                        <span className="marke marke--warn">abgelehnt</span>
                        <span className="hilfe"> {alt.grund}</span>
                      </>
                    ) : (
                      <>
                        <span className="marke marke--warn">Vorschlag</span>
                        <span className="hilfe">
                          {' '}
                          {alt.vorschlagTag === null ? '' : alsTag(alt.vorschlagTag)} {alt.grund}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
